<template>
  <div class="link-bank-container">
    <!-- Loading state -->
    <div v-if="isLoading" class="loading-container">
      <i class="el-icon-loading" style="font-size: 48px; color: #409eff;"></i>
      <p>Đang tạo phiên liên kết...</p>
    </div>

    <!-- Hosted Link Container -->
    <div v-else-if="hostedLinkUrl" id="bankhub-container" class="bankhub-container">
      <!-- SDK sẽ render ở đây -->
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="error-container">
      <i class="el-icon-circle-close" style="font-size: 48px; color: #f56c6c;"></i>
      <p>{{ error }}</p>
      <el-button type="primary" @click="retryCreateLinkToken">Thử lại</el-button>
      <el-button @click="handleClose">Đóng</el-button>
    </div>
  </div>
</template>

<script>
import MerchantConfigService from '@/services/MerchantConfigService';
import NotifyMessage from "@/commonFuncion/NotifyMessage";

export default {
  name: 'LinkBankAccountDialog',
  props: {
    isLinkBankAccountDialog: {
      type: Boolean,
      default: false
    },
    companyXid: {
      type: String,
      required: true
    },
    purpose: {
      type: String,
      default: 'LINK_BANK_ACCOUNT',
      validator: (value) => ['LINK_BANK_ACCOUNT', 'UNLINK_BANK_ACCOUNT'].includes(value)
    },
    bankAccountXid: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      isLoading: false,
      hostedLinkUrl: '',
      linkToken: '',
      error: '',
      bankhubInstance: null
    };
  },

  watch: {
    isLinkBankAccountDialog(newVal) {
      if (newVal) {
        this.createLinkToken();
      } else {
        this.handleClose();
      }
    }
  },

  methods: {
    createLinkToken() {
      if (!this.companyXid) {
        this.error = 'Company XID chưa được cấu hình. Vui lòng liên hệ quản trị viên.';
        this.isLoading = false;
        return;
      }

      if (this.purpose === 'UNLINK_BANK_ACCOUNT' && !this.bankAccountXid) {
        this.error = 'Thiếu bank_account_xid để hủy liên kết tài khoản.';
        this.isLoading = false;
        return;
      }

      this.isLoading = true;
      this.error = '';

      const payload = {
        company_xid: this.companyXid,
        purpose: this.purpose,
        completion_redirect_uri: "",
        is_mobile_app: 0,
        language: 'vi'
      };

      if (this.purpose === 'UNLINK_BANK_ACCOUNT') {
        payload.bank_account_xid = this.bankAccountXid;
      }

      const request = this.purpose === 'UNLINK_BANK_ACCOUNT'
        ? MerchantConfigService.createLinkTokenUnlink(payload)
        : MerchantConfigService.createLinkToken(payload);

      request
          .then(response => {
            if (response.data && response.data.data) {
              this.hostedLinkUrl = response.data.data.hosted_link_url;
              this.linkToken = response.data.data.link_token;

              this.$nextTick(() => {
                this.initBankhubSDK();
              });
            }
            this.isLoading = false;
          })
          .catch(error => {
            this.isLoading = false;
            console.error('Lỗi khi tạo link token:', error);

            if (error.response) {
              const errorData = error.response.data;

              if (errorData && errorData.message) {
                this.error = errorData.message;
              } else if (error.response.status === 403) {
                this.error = 'Không thể kết nối đến SePay API. Vui lòng liên hệ quản trị viên để kiểm tra cấu hình IP Whitelist.';
              } else if (error.response.status === 401) {
                this.error = 'Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại.';
              } else if (error.response.status === 400) {
                this.error = 'Dữ liệu không hợp lệ. Vui lòng kiểm tra lại Company XID.';
              } else {
                this.error = `Lỗi ${error.response.status}: Không thể tạo phiên liên kết.`;
              }
            } else {
              this.error = 'Không thể kết nối đến server. Vui lòng kiểm tra kết nối mạng.';
            }
          });
    },

    initBankhubSDK() {
      if (typeof window.Bankhub === 'undefined') {
        this.error = 'SDK chưa được tải. Vui lòng refresh trang.';
        return;
      }

      try {
        this.bankhubInstance = new window.Bankhub({
          mode: 'embedded',
          container: '#bankhub-container',

          onSuccess:  (result) => {
            const successMsg = this.purpose === 'UNLINK_BANK_ACCOUNT'
              ? 'Hủy liên kết tài khoản thành công!'
              : 'Liên kết tài khoản thành công!';
            NotifyMessage.leftCornerNotificationSuccess(successMsg);
            this.$emit('success', result);
            this.handleClose();
          },

          onExit: (result) => {
            console.log('❌ User thoát Bank Hub', result);
            this.handleClose();
          },

          onOpen: (data) => {
            console.log('🔓 Bank Hub đã mở', data);
          },

          onExpired: (data) => {
            console.log('⏰ Token/Session hết hạn', data);
            NotifyMessage.leftCornerNotificationError(`Token hoặc Session đã hết hạn! (${data.expired_type})`);
            this.handleClose();
          }
        });

        this.bankhubInstance.initLink(this.hostedLinkUrl);
        this.bankhubInstance.open();

      } catch (error) {
        this.error = 'Không thể khởi tạo SDK. Vui lòng thử lại.';
      }
    },

    retryCreateLinkToken() {
      this.createLinkToken();
    },

    handleClose() {
      if (this.bankhubInstance) {
        try {
          this.bankhubInstance.destroy();
        } catch (error) {
          console.error('Lỗi khi destroy SDK:', error);
        }
        this.bankhubInstance = null;
      }

      this.hostedLinkUrl = '';
      this.linkToken = '';
      this.error = '';
      this.isLoading = false;

      this.$emit('closeDialog');
    }
  },

  beforeDestroy() {
    if (this.bankhubInstance) {
      try {
        this.bankhubInstance.destroy();
      } catch (error) {
        console.error('Lỗi khi destroy SDK:', error);
      }
    }
  }
};
</script>

<style scoped lang="scss">
.link-bank-container {
  width: 100%;
  padding: 20px;
}

.loading-container,
.error-container,
.initial-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;

  p {
    margin-top: 15px;
    font-size: 14px;
    color: #606266;
  }
}

.error-container {
  p {
    color: #f56c6c;
    margin-bottom: 20px;
  }
}

.bankhub-container {
  width: 100%;
  min-height: 500px;
}
</style>
